import React, { useState, useRef, useEffect } from 'react';
import { uploadDocument } from '../../api/client';

// ── Product catalog (matches your SharePoint folders) ────────────────────────
const PRODUCTS = [
  'GeneralInformation',
  'ESOne_Mobile',
  'InSync',
  'Perform',
  'ShadowLink',
  'ShadowPrint',
  'InstaNote',
  'Inquiry',
  'Inscribe',
  'InCommand',
  'InTouch',
];

// ── Map any folder name (case-insensitive) to a canonical product ────────────
const productFromFolder = (folder) => {
  const key = (folder || '').toLowerCase().replace(/[\s_-]/g, '');
  for (const p of PRODUCTS) {
    if (p.toLowerCase().replace(/[\s_-]/g, '') === key) return p;
  }
  return null;   // unknown
};

const MAX_PARALLEL = 3;

const s = {
  card: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f0fafa 60%, #e6f7f7 100%)',
    border: '1px solid rgba(29, 156, 156, 0.18)',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 4px 24px rgba(13, 79, 82, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
    position: 'relative',
    overflow: 'hidden',
  },
  cardAccent: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0d4f52, #1d9c9c, #4fd1c5)',
  },
  title: { fontSize: '20px', fontWeight: 800, marginBottom: '6px', color: '#0f172a', letterSpacing: '-0.01em' },
  subtitle: { color: '#64748b', fontSize: '13px', marginBottom: '22px' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '22px', padding: '4px', background: 'rgba(255,255,255,0.6)', borderRadius: '12px', border: '1px solid rgba(29, 156, 156, 0.12)', width: 'fit-content' },
  tab: (active) => ({
    padding: '8px 18px',
    background: active ? '#ffffff' : 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: active ? '#0d7377' : '#64748b',
    fontSize: '13px',
    fontWeight: active ? 700 : 500,
    cursor: 'pointer',
    transition: 'all 0.18s',
    boxShadow: active ? '0 2px 8px rgba(13, 79, 82, 0.08)' : 'none',
  }),
  label: { display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' },
  chip: (sel) => ({
    padding: '7px 16px',
    borderRadius: '999px',                             // pure capsule pill
    border: '1px solid ' + (sel ? 'transparent' : 'rgba(29, 156, 156, 0.25)'),
    background: sel
      ? 'linear-gradient(135deg, #1d9c9c 0%, #0d7377 100%)'
      : 'rgba(255, 255, 255, 0.85)',
    color: sel ? '#ffffff' : '#475569',
    fontSize: '12.5px',
    fontWeight: sel ? 700 : 500,
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    userSelect: 'none',
    boxShadow: sel
      ? '0 4px 12px rgba(13, 115, 119, 0.25), 0 1px 2px rgba(0,0,0,0.05)'
      : '0 1px 2px rgba(0,0,0,0.03)',
    letterSpacing: '0.01em',
  }),
  dropzone: (drag) => ({
    border: `2px dashed ${drag ? '#1d9c9c' : 'rgba(29, 156, 156, 0.28)'}`,
    borderRadius: '14px',
    padding: '48px 24px',
    textAlign: 'center',
    background: drag
      ? 'linear-gradient(135deg, #e6f7f7 0%, #d1f0f0 100%)'
      : 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backdropFilter: 'blur(4px)',
  }),
  queue: { marginTop: '20px' },
  queueHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '12px', color: 'var(--text-secondary)' },
  queueItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    marginBottom: '6px',
    fontSize: '12.5px',
  },
  productBadge: {
    padding: '2px 8px',
    background: 'var(--accent-light)',
    color: 'var(--accent)',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 700,
  },
  progressBar: (pct, status) => ({
    height: '4px',
    background: status === 'done' ? 'var(--success)' : status === 'error' ? 'var(--error)' : 'var(--accent)',
    width: `${pct}%`,
    borderRadius: '2px',
    transition: 'width 0.3s',
  }),
  progressTrack: { flex: 1, height: '4px', background: 'var(--bg-input)', borderRadius: '2px', overflow: 'hidden' },
};

const statusIcon = (st) => ({ uploading: '⏳', queued: '📋', done: '✅', error: '❌' }[st] || '📄');

/* ─── One-time keyframes for the fun upload panel ─────────────────────── */
if (typeof document !== 'undefined' && !document.getElementById('up-keyframes')) {
  const el = document.createElement('style');
  el.id = 'up-keyframes';
  el.textContent = `
    @keyframes upBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
    @keyframes upSpin   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    @keyframes upWiggle { 0%,100%{transform:rotate(-8deg)} 50%{transform:rotate(8deg)} }
    @keyframes upStripes{ from{background-position:0 0} to{background-position:40px 0} }
    @keyframes upDots   { 0%{content:'.'} 33%{content:'..'} 66%{content:'...'} 100%{content:''} }
    @keyframes upFade   { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:none} }
  `;
  document.head.appendChild(el);
}

const FUN_MESSAGES = [
  '🐢 Slow and steady — chunking your PDFs…',
  '🧠 Teaching the AI new tricks…',
  '🔍 Reading every line so you don\'t have to…',
  '☕ Grab a coffee, this is on us…',
  '🛠️ Hammering paragraphs into chunks…',
  '🚀 Almost there — hold tight!',
  '🪄 Sprinkling embedding pixie dust…',
  'Stuffing words into the vector store…',
];

function UploadProgressPanel({ stats }) {
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % FUN_MESSAGES.length), 2600);
    return () => clearInterval(t);
  }, []);

  const total = stats.total || 1;
  const completed = stats.done + stats.err;
  const pct = Math.round((completed / total) * 100);
  const allDone = completed === total && stats.inflight === 0 && stats.waiting === 0;

  return (
    <div style={{
      marginTop: 18,
      padding: '22px 24px',
      borderRadius: 18,
      background: 'linear-gradient(135deg, #f0fafa 0%, #e6f7f7 50%, #ffffff 100%)',
      border: '1px solid rgba(29,156,156,0.25)',
      boxShadow: '0 10px 32px rgba(13,79,82,0.10), 0 2px 6px rgba(0,0,0,0.04)',
      position: 'relative', overflow: 'hidden',
      animation: 'upFade 0.3s ease',
    }}>
      {/* top accent */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: 'linear-gradient(90deg, #0d4f52, #1d9c9c, #4fd1c5)',
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {allDone && (
          <div style={{
            fontSize: 42, lineHeight: 1,
            animation: 'upWiggle 0.6s ease-in-out 2',
          }}>🎉</div>
        )}

        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 16, fontWeight: 800, color: '#0d4f52', letterSpacing: '-0.01em',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {allDone
              ? 'All set! Your documents are indexed and searchable.'
              : <>Please wait — work in progress<DotsAnimation /></>}
          </div>
          <div style={{ fontSize: 12.5, color: '#475569', marginTop: 4, minHeight: 18 }}>
            {!allDone && (
              <span key={msgIdx} style={{ animation: 'upFade 0.4s ease' }}>
                {FUN_MESSAGES[msgIdx]}
              </span>
            )}
          </div>
        </div>

        {/* Big count badges */}
        <div style={{ display: 'flex', gap: 8 }}>
          <StatBadge label="Total"      value={stats.total}    color="#0d7377" />
          <StatBadge label="Processing" value={stats.inflight} color="#d97706" pulse={stats.inflight > 0} />
          <StatBadge label="Queued"     value={stats.waiting}  color="#64748b" />
          <StatBadge label="Done"       value={stats.done}     color="#16a34a" />
          {stats.err > 0 && <StatBadge label="Errors" value={stats.err} color="#dc2626" />}
        </div>
      </div>

      {/* Animated progress bar with diagonal stripes */}
      <div style={{
        marginTop: 18,
        height: 14, borderRadius: 999,
        background: 'rgba(13,79,82,0.08)',
        overflow: 'hidden',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)',
      }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: `
            repeating-linear-gradient(45deg,
              rgba(255,255,255,0.18) 0 10px,
              rgba(255,255,255,0)   10px 20px),
            linear-gradient(90deg, #1d9c9c, #0d7377)
          `,
          backgroundSize: '40px 40px, 100% 100%',
          animation: allDone ? 'none' : 'upStripes 1.2s linear infinite',
          transition: 'width 0.5s ease',
          borderRadius: 999,
          boxShadow: '0 0 8px rgba(29,156,156,0.4)',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>
        <span>{completed} / {stats.total} indexed</span>
        <span>{pct}%</span>
      </div>
    </div>
  );
}

function StatBadge({ label, value, color, pulse }) {
  return (
    <div style={{
      minWidth: 64,
      padding: '8px 12px',
      borderRadius: 10,
      background: '#ffffff',
      border: `1px solid ${color}33`,
      textAlign: 'center',
      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
      animation: pulse ? 'upBounce 1.4s ease-in-out infinite' : 'none',
    }}>
      <div style={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 3 }}>{label}</div>
    </div>
  );
}

function DotsAnimation() {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 420);
    return () => clearInterval(t);
  }, []);
  return <span style={{ width: 14, display: 'inline-block', color: '#1d9c9c' }}>{dots}</span>;
}

export default function UploadPanel({ onUploadDone }) {
  const mode = 'single';                            // folder bulk import removed
  const [selected, setSelected] = useState([]);     // products for single mode
  const [queue, setQueue] = useState([]);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const idCounter = useRef(0);
  const active = useRef(0);
  const pending = useRef([]);

  // ── Concurrency-limited upload pump ─────────────────────────────────────
  const pump = () => {
    while (active.current < MAX_PARALLEL && pending.current.length > 0) {
      const job = pending.current.shift();
      active.current++;
      doUpload(job).finally(() => {
        active.current--;
        pump();
      });
    }
  };

  const doUpload = async (job) => {
    setQueue((q) => q.map((i) => (i.id === job.id ? { ...i, status: 'uploading' } : i)));
    try {
      await uploadDocument(
        job.file,
        (pct) => setQueue((q) => q.map((i) => (i.id === job.id ? { ...i, progress: pct } : i))),
        job.products,
      );
      setQueue((q) => q.map((i) => (i.id === job.id ? { ...i, progress: 100, status: 'done' } : i)));
      onUploadDone?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      setQueue((q) => q.map((i) => (i.id === job.id ? { ...i, status: 'error', error: msg } : i)));
    }
  };

  const enqueue = (items) => {
    if (items.length === 0) return;
    const newItems = items.map((it) => ({
      id: ++idCounter.current,
      file: it.file,
      name: it.file.name,
      products: it.products,
      progress: 0,
      status: 'queued',
    }));
    setQueue((q) => [...q, ...newItems]);
    pending.current.push(...newItems);
    pump();
  };

  // ── Single-mode handlers ────────────────────────────────────────────────
  const handleSingleFiles = (files) => {
    const pdfs = Array.from(files).filter((f) => f.type === 'application/pdf');
    if (pdfs.length === 0) return alert('Only PDF files are supported.');
    if (selected.length === 0) {
      if (!window.confirm('No product selected. Upload anyway with no tag?')) return;
    }
    enqueue(pdfs.map((file) => ({ file, products: selected })));
  };


  // ── Stats ──────────────────────────────────────────────────────────────
  const stats = {
    total: queue.length,
    done: queue.filter((q) => q.status === 'done').length,
    err: queue.filter((q) => q.status === 'error').length,
    inflight: queue.filter((q) => q.status === 'uploading').length,
    waiting: queue.filter((q) => q.status === 'queued').length,
  };

  return (
    <div style={s.card}>
      <div style={s.cardAccent} />
      <h2 style={s.title}>Upload Documents</h2>
      <p style={s.subtitle}>PDF files only · Max 100 MB per file · Tag with product(s) to enable filtered search</p>

      {mode === 'single' && (
        <>
          <label style={s.label}>Tag with product(s)</label>
          <div style={s.chipRow}>
            {PRODUCTS.map((p) => (
              <span
                key={p}
                style={s.chip(selected.includes(p))}
                onClick={() =>
                  setSelected((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p])
                }
              >
                {selected.includes(p) ? '✓ ' : ''}{p}
              </span>
            ))}
          </div>

          <div
            style={s.dropzone(drag)}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); handleSingleFiles(e.dataTransfer.files); }}
          >
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📁</div>
            <div style={{ fontWeight: 600, marginBottom: '6px' }}>
              {drag ? 'Drop PDFs here' : 'Click or drag & drop PDFs'}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              {selected.length > 0
                ? `Will be tagged: ${selected.join(', ')}`
                : 'No products selected — files will be untagged'}
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { handleSingleFiles(e.target.files); e.target.value = ''; }}
          />
        </>
      )}

      {/* ── Fun progress panel ──────────────────────────────────────── */}
      {queue.length > 0 && <UploadProgressPanel stats={stats} />}
    </div>
  );
}
